const HTTP_METHODS = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options', 'trace'] as const;

/** Path segments that are not `{param}` placeholders. */
function staticPathSegments(path: string): string[] {
    return path.split('/').filter((s) => s.length > 0 && !/^\{[^}]+\}$/.test(s));
}

function titleCaseWords(slug: string): string {
    return slug
        .split('-')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
}

/**
 * One display tag per path prefix so Swagger UI groups operations into manageable sections.
 * Master data uses short resource names (e.g. "Purposes") plus a shared tag description in the spec.
 */
export function moduleTagForPath(path: string): string {
    const segs = staticPathSegments(path);
    if (segs.length === 0) return 'API';

    const [a0, a1, a2] = segs;

    if (a0 === 'health') return 'System';
    if (a0 === 'auth') return 'Authentication';

    if (a0 === 'collectionpoints') {
        if (segs.includes('consent')) return 'Consent submission';
        return 'Collection points';
    }

    if (a0 === 'masterdata' && a1) {
        return titleCaseWords(a1);
    }

    if (a0 === 'clients') {
        if (segs.length === 1) return 'Clients';
        return `Clients · ${titleCaseWords(a1!)}`;
    }

    if (a0 === 'api-keys') {
        const tail = segs.slice(1).map(titleCaseWords).join(' · ');
        return tail ? `API keys · ${tail}` : 'API keys';
    }

    if (a0 === 'audit-logs') return 'Audit logs';

    if (a0 === 'admin' && a1) {
        const base = `Admin · ${titleCaseWords(a1)}`;
        if (a2) return `${base} · ${titleCaseWords(a2)}`;
        return base;
    }

    if (a0 === 'translation') return 'Translation';
    if (a0 === 'vault') return 'Vault';
    if (a0 === 'test') return 'Test utilities';
    if (a0 === 'contact') return 'Contact';
    if (a0 === 'webhooks') return 'Webhooks';

    if (a0 === 'portal') {
        if (!a1) return 'Portal';
        if (a1 === 'collection-points') {
            if (a2) return `Portal · Collection points · ${titleCaseWords(a2)}`;
            return 'Portal · Collection points';
        }
        const base = `Portal · ${titleCaseWords(a1)}`;
        if (a2) return `${base} · ${titleCaseWords(a2)}`;
        return base;
    }

    if (a0 === 'notices') {
        if (segs.length === 1) return 'Notices';
        return `Notices · ${titleCaseWords(a1!)}`;
    }

    if (a1) return `${titleCaseWords(a0)} · ${titleCaseWords(a1)}`;
    return titleCaseWords(a0);
}

function sortTagsForDisplay(tags: string[], masterdataTagNames: Set<string>): string[] {
    const priority = (name: string): [number, string] => {
        if (name === 'System') return [0, name];
        if (name === 'Authentication') return [1, name];
        if (name === 'Collection points') return [2, name];
        if (name === 'Consent submission') return [3, name];
        if (masterdataTagNames.has(name)) return [10, name];
        if (name === 'Clients') return [18, name];
        if (name.startsWith('Clients ·')) return [20, name];
        if (name.startsWith('API keys')) return [30, name];
        if (name === 'Audit logs') return [40, name];
        if (name.startsWith('Admin ·')) return [50, name];
        if (name === 'Translation') return [60, name];
        if (name === 'Vault') return [61, name];
        if (name.startsWith('Notices')) return [70, name];
        if (name.startsWith('Portal ·')) return [80, name];
        if (name === 'Webhooks') return [90, name];
        if (name === 'Contact') return [91, name];
        if (name === 'Test utilities') return [100, name];
        return [200, name];
    };

    return [...tags].sort((a, b) => {
        const [pa, sa] = priority(a);
        const [pb, sb] = priority(b);
        if (pa !== pb) return pa - pb;
        return sa.localeCompare(sb);
    });
}

/**
 * Rewrites each operation's `tags` from the URL path and sets root `tags` for Swagger UI order.
 */
export function applyPathModuleTags(spec: Record<string, unknown>): void {
    const paths = spec.paths as Record<string, Record<string, unknown>> | undefined;
    if (!paths || typeof paths !== 'object') return;

    const tagNames = new Set<string>();
    const masterdataTagNames = new Set<string>();

    for (const pathKey of Object.keys(paths)) {
        const pathItem = paths[pathKey];
        if (!pathItem || typeof pathItem !== 'object') continue;

        const tagName = moduleTagForPath(pathKey);
        if (pathKey.startsWith('/masterdata/')) {
            masterdataTagNames.add(tagName);
        }

        for (const method of HTTP_METHODS) {
            const op = pathItem[method];
            if (!op || typeof op !== 'object') continue;
            (op as Record<string, unknown>).tags = [tagName];
            tagNames.add(tagName);
        }
    }

    const ordered = sortTagsForDisplay(Array.from(tagNames), masterdataTagNames);
    spec.tags = ordered.map((name) =>
        masterdataTagNames.has(name) ? { name, description: 'Master data' } : { name }
    );
}
